import { useState, useEffect } from "react";
import { Modal, Avatar } from "../../../Component/Workshop/UI";
import { useQuery } from "@tanstack/react-query";
import axios from "../../../axiosInstance";  
import {useVendorDrivers} from "../../../hooks/useVendorQueries.js";

// ─── Tiny sub-components ─────────────────────────────────────────────────────

function DriverStatusPill({ isWorking, isActive }) {
  if (!isActive)   return <span style={pill("var(--wd-t3)",  "#f1f1f1")}>Inactive</span>;
  if (isWorking)   return <span style={pill("#b45309",        "#fef3c7")}>On job</span>;
  return               <span style={pill("#15803d",        "#dcfce7")}>Available</span>;
}

function pill(color, bg) {
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    color,
    background: bg,
    whiteSpace: "nowrap",
  };
}

function ExpiryBadge({ label, info }) {
  if (!info) return null;
  const color = info.expired ? "var(--wd-red)" : info.expiringSoon ? "#b45309" : "var(--wd-t3)";
  const text  = info.expired
    ? `${label}: expired`
    : info.expiringSoon
    ? `${label}: ${info.daysLeft}d left`
    : null;
  if (!text) return null;
  return (
    <span style={{ fontSize: 10, color, marginRight: 4, fontWeight: 600 }}>
      ⚠ {text}
    </span>
  );
}

function DriverCard({ driver }) {
  const available = driver.isActive && !driver.isWorking;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "10px 12px",
      borderRadius: 10,
      border: "0.5px solid var(--wd-border)",
      background: available ? "rgba(21,128,61,.04)" : "var(--wd-hover)",
      opacity: driver.isActive ? 1 : 0.6,
    }}>
      {/* Avatar */}
      <Avatar name={driver.driverName || driver.vehicleNo} size={38} />

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--wd-t1)" }}>
            {driver.driverName || "—"}
          </span>
          <DriverStatusPill isWorking={driver.isWorking} isActive={driver.isActive} />
        </div>

        <div style={{ fontSize: 12, color: "var(--wd-t2)", marginTop: 2, fontFamily: "var(--wd-mono)" }}>
          {driver.vehicleNo}
          {driver.chooseFromSelectedService && (
            <span style={{ marginLeft: 8, color: "var(--wd-t3)" }}>
              · {driver.chooseFromSelectedService}
            </span>
          )}
        </div>

        {driver.driverNumber && (
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>
            📞 {driver.driverNumber}
          </div>
        )}

        {/* Doc expiry warnings */}
        <div style={{ marginTop: 4 }}>
          <ExpiryBadge label="Insurance" info={driver.expiries?.insuranceExp} />
          <ExpiryBadge label="Fitness"   info={driver.expiries?.fitnessExp}   />
          <ExpiryBadge label="Pollution" info={driver.expiries?.pollutionExp} />
        </div>

        {/* Active case badge */}
        {driver.activeCase && (
          <div style={{
            marginTop: 5, fontSize: 11,
            padding: "3px 8px", borderRadius: 6,
            background: "rgba(234,88,12,.08)",
            color: "#b45309", display: "inline-block",
          }}>
            🚛 On case: {driver.activeCase.code}
          </div>
        )}
      </div>

      {/* Location freshness */}
      {driver.latitude && (
        <a
          href={`https://maps.google.com?q=${driver.latitude},${driver.longitude}`}
          target="_blank" rel="noreferrer"
          style={{ fontSize: 11, color: "var(--wd-teal)", whiteSpace: "nowrap", flexShrink: 0 }}
          title="Open in maps"
        >
          📍 map
        </a>
      )}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export function AssignCaseModal({
  vendor: v,
  defaultCaseNo,
  defaultServiceType,
  onClose,
  onAssign,
}) {
  const [step,       setStep]       = useState(1);   // 1 = case code, 2 = drivers (skipped for advocates)
  const [caseNo,     setCaseNo]     = useState("");
  const [error,      setError]      = useState("");
  const [driverFilter, setDriverFilter] = useState("all"); // all | available | working

  // Reset when modal opens
  useEffect(() => {
    if (v) {
      setCaseNo(defaultCaseNo || "");
      const isAdvocate = v.vendorType === "advocate";
      setStep(defaultCaseNo && !isAdvocate ? 2 : 1);  // if pre-filled, go straight to step 2 (non-advocates only)
      setError("");
      setDriverFilter("all");
    }
  }, [v, defaultCaseNo]);

  // Fetch drivers only when we enter step 2 — never fires for advocates,
  // since they never leave step 1 below.
  const isAdvocate = v?.vendorType === "advocate";
  const {
    data:      driversData,
    isLoading: driversLoading,
    isError:   driversError,
  } = useVendorDrivers(v?.vendorCode, step === 2 && !isAdvocate);

  if (!v) return null;

  const typeMap = {
    crane:               "Crane",
    recoveryVan:         "Recovery Van",
    craneAndRecoveryVan: "Crane & Recovery",
    advocate:            "Advocate", 
  };

  const allDrivers  = driversData?.data || [];
  const available   = driversData?.available ?? 0;
  const total       = driversData?.total ?? 0;

  const displayedDrivers = allDrivers.filter(d => {
    if (driverFilter === "available") return d.isActive && !d.isWorking;
    if (driverFilter === "working")   return d.isWorking;
    return true;
  });

  // ── Step 1: confirm / enter the case code ─────────────────────────────────
  // For advocates, this button sends the assignment directly — there's no
  // driver pool to check, so there's no step 2 to advance to.
  const step1Footer = (
    <>
      <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
      <button
        className="wd-btn wd-btn-primary"
        disabled={!caseNo.trim()}
        onClick={() => {
          setError("");
          if (isAdvocate) {
            onAssign(caseNo);
          } else {
            setStep(2);
          }
        }}
      >
        {isAdvocate ? "Assign Vendor" : "Next → Check Drivers"}
      </button>
    </>
  );

  // ── Step 2: show drivers + confirm (crane/recovery vendors only) ──────────
  const step2Footer = (
    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
      <button className="wd-btn wd-btn-ghost" onClick={() => setStep(1)}>
        ← Back
      </button>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
        <button
          className="wd-btn wd-btn-primary"
          onClick={() => onAssign(caseNo)}
          disabled={!caseNo.trim()}
        >
          Send Request to Vendor
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      open={!!v}
      onClose={onClose}
      title={step === 1
        ? `Assign Case → ${v.vendorName}`
        : `Driver Availability — ${v.vendorName}`}
      maxWidth={560}
      footer={step === 1 ? step1Footer : step2Footer}
    >
      {/* ── Inactive warning ── */}
      {!(v.isActive === "1" || v.isActive === 1 || v.isActive === true) && (
        <div style={{
          display: "flex", gap: 10, padding: "10px 14px",
          background: "rgba(239,68,68,.08)", borderRadius: 10,
          border: "1px solid rgba(239,68,68,.15)",
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span style={{ fontSize: 13, color: "var(--wd-red)" }}>
            This vendor is currently <strong>Inactive</strong>. Assigning a case may cause delays.
          </span>
        </div>
      )}

      {/* ── Vendor summary (shown on both steps) ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 14px", background: "var(--wd-hover)", borderRadius: 10,
      }}>
        <Avatar name={v.vendorName} size={40} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "var(--wd-t1)" }}>{v.vendorName}</div>
          <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 2 }}>
            📍 {v.district}, {v.state}
            {v.vendorType && <> · 🚛 {typeMap[v.vendorType] || v.vendorType}</>}
          </div>
          {v.vendorPhone && (
            <div style={{ fontSize: 12, color: "var(--wd-t2)", marginTop: 2, fontFamily: "var(--wd-mono)" }}>
              📞 {v.vendorPhone}
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          STEP 1 — case code input
      ════════════════════════════════════════════════════════════════════ */}
      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-t2)" }}>
            Case / Request Code *
          </label>

          {defaultCaseNo ? (
            <div style={{
              padding: "8px 12px",
              background: "rgba(59,130,246,.08)",
              border: "1px solid rgba(59,130,246,.2)",
              borderRadius: 8, fontSize: 13,
              fontFamily: "var(--wd-mono)", color: "var(--wd-blue)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              🔗 {defaultCaseNo}
              <span style={{ fontSize: 11, color: "var(--wd-t3)", fontFamily: "inherit" }}>
                · pre-filled from case
              </span>
            </div>
          ) : (
            <input
              className="wd-input"
              placeholder="e.g. QS-1023"
              value={caseNo}
              onChange={e => { setCaseNo(e.target.value); setError(""); }}
              autoFocus
            />
          )}

          {error && <span style={{ fontSize: 11, color: "var(--wd-red)" }}>{error}</span>}

          {isAdvocate && (
            <div style={{
              marginTop: 4, padding: "10px 14px", background: "rgba(59,130,246,.07)",
              borderRadius: 10, border: "1px solid rgba(59,130,246,.15)",
            }}>
              <span style={{ fontSize: 12, color: "var(--wd-blue)" }}>
                ℹ️ Advocates don't have a driver pool to check — clicking <strong>Assign Vendor</strong> sends the case straight to them.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          STEP 2 — driver availability (read-only info panel, crane/recovery only)
      ════════════════════════════════════════════════════════════════════ */}
      {step === 2 && (
        <>
          {/* Case code pill */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 12px", borderRadius: 8, fontSize: 13,
            background: "rgba(59,130,246,.07)",
            border: "1px solid rgba(59,130,246,.15)",
          }}>
            <span style={{ color: "var(--wd-blue)", fontFamily: "var(--wd-mono)", fontWeight: 600 }}>
              🔗 {caseNo}
            </span>
            <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>will be sent to this vendor</span>
          </div>

          {/* Availability summary bar */}
          {!driversLoading && !driversError && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 12px", borderRadius: 8,
              background: available > 0 ? "rgba(21,128,61,.06)" : "rgba(239,68,68,.06)",
              border: `1px solid ${available > 0 ? "rgba(21,128,61,.2)" : "rgba(239,68,68,.2)"}`,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600,
                color: available > 0 ? "#15803d" : "var(--wd-red)" }}>
                {available > 0
                  ? `✅ ${available} of ${total} driver${total !== 1 ? "s" : ""} available`
                  : `⚠️ No drivers currently available (${total} total)`}
              </div>
              <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>
                Vendor will assign a driver
              </span>
            </div>
          )}

          {/* Filter pills */}
          {!driversLoading && allDrivers.length > 0 && (
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { key: "all",       label: `All (${total})`         },
                { key: "available", label: `Available (${available})`},
                { key: "working",   label: `On job (${total - available})`},
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  style={driverFilter === key ? {
                    borderColor: "var(--wd-blue)", color: "var(--wd-blue)",
                    background: "rgba(59,130,246,.08)",
                  } : {}}
                  onClick={() => setDriverFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Driver list */}
          <div style={{
            display: "flex", flexDirection: "column", gap: 8,
            maxHeight: 300, overflowY: "auto",
            paddingRight: 4,
          }}>
            {driversLoading && (
              <div style={{ textAlign: "center", padding: 24, color: "var(--wd-t3)", fontSize: 13 }}>
                Loading drivers…
              </div>
            )}

            {driversError && (
              <div style={{ textAlign: "center", padding: 16, color: "var(--wd-red)", fontSize: 13 }}>
                Failed to load driver info. You can still send the request.
              </div>
            )}

            {!driversLoading && !driversError && allDrivers.length === 0 && (
              <div style={{ textAlign: "center", padding: 24, color: "var(--wd-t3)", fontSize: 13 }}>
                No drivers registered for this vendor yet.
              </div>
            )}

            {!driversLoading && displayedDrivers.map(d => (
              <DriverCard key={d.vendorDriverCode} driver={d} />
            ))}

            {!driversLoading && allDrivers.length > 0 && displayedDrivers.length === 0 && (
              <div style={{ textAlign: "center", padding: 12, color: "var(--wd-t3)", fontSize: 12 }}>
                No drivers match this filter.
              </div>
            )}
          </div>

          {/* Info banner */}
          <div style={{
            padding: "10px 14px", background: "rgba(59,130,246,.07)",
            borderRadius: 10, border: "1px solid rgba(59,130,246,.15)",
          }}>
            <span style={{ fontSize: 12, color: "var(--wd-blue)" }}>
              ℹ️ Clicking <strong>Send Request to Vendor</strong> will notify the vendor's app.
              The vendor will then assign one of their available drivers.
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}